"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { Menu, X } from "lucide-react";
import { WEB_APP_LINKS } from "@/lib/constants";
import type { SiteMenu } from "@/lib/nav";
import { AppStoreBadges } from "./AppStoreBadges";
import { BrandMark } from "./BrandMark";
import { NavDropdown } from "./NavDropdown";
// Single source for the hash-scroll machinery — the Footer's HashLink uses
// the same scrollToId + pending key, so footer and navbar hash clicks can't
// drift apart again (the footer's plain links reintroduced the stuck-scroll
// bug the navbar had already fixed).
import { PENDING_SCROLL_KEY, scrollToId } from "./HashLink";

// Translated words run longer than the English ones (Spanish, Russian and Tamil don't
// fit at 768 px), so other languages keep the phone menu up to 1024 px.
const BREAK = {
  en: { center: "md:flex", inline: "md:inline-flex", phoneOnly: "md:hidden" },
  translated: { center: "lg:flex", inline: "lg:inline-flex", phoneOnly: "lg:hidden" },
};

/** The site header in every language; the layouts pass siteMenu(locale) from lib/nav.ts. */
export function Navbar({ menu }: { menu: SiteMenu }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const at = menu.englishHrefLang ? BREAK.translated : BREAK.en;
  const pathname = usePathname();
  const router = useRouter();

  // "/#anchor" links never put the hash in the URL: a URL fragment makes the
  // browser re-anchor to the target on load/layout shifts (users got stuck
  // unable to scroll back to the top on "/#download"), and every reload lands
  // there again. Same-page: plain JS scroll. Cross-page: navigate to "/" and
  // scroll after the home sections mount. block:"start" so both paths obey
  // the targets' scroll-mt offsets and land identically.
  const scrollToHash = (href: string) => (e: React.MouseEvent) => {
    if (!href.startsWith("/#")) return;
    e.preventDefault();
    const id = href.slice(2);
    if (pathname === "/") {
      scrollToId(id);
    } else {
      sessionStorage.setItem(PENDING_SCROLL_KEY, id);
      router.push("/");
    }
  };

  useEffect(() => {
    if (pathname !== "/") return;
    const id = sessionStorage.getItem(PENDING_SCROLL_KEY);
    if (id) {
      sessionStorage.removeItem(PENDING_SCROLL_KEY);
      // two frames so the home sections exist; one retry for slow mounts
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          if (!scrollToId(id)) setTimeout(() => scrollToId(id), 150);
        }),
      );
    } else if (window.location.hash) {
      // Residual "/#hash" arrivals (footer links, old shared URLs): the native
      // fragment jump also scrolls overflow-hidden ancestors (clipping the
      // hero) — reposition via scrollToId (which repairs those offsets), then
      // strip the fragment so it can't re-anchor and a reload starts at top.
      const id = window.location.hash.slice(1);
      const t = setTimeout(() => {
        scrollToId(id);
        history.replaceState(null, "", window.location.pathname);
      }, 400);
      return () => clearTimeout(t);
    }
  }, [pathname]);

  return (
    <>
      <header className="sticky top-0 z-50 border-b border-border/50 bg-card/80 backdrop-blur-md">
        <nav className="mx-auto flex max-w-7xl items-center px-4 py-3 sm:px-6">
          {/* Logo */}
          <Link href="/" hrefLang={menu.englishHrefLang} className="flex shrink-0 items-center gap-2.5">
            <BrandMark width={38} className="h-auto w-[38px]" priority />
            <span className="text-xl font-bold tracking-tight text-foreground">
              Helibrain
            </span>
          </Link>

          {/* Desktop nav — centered links */}
          <div className={`mx-3 hidden flex-1 items-center justify-center gap-3 lg:mx-4 lg:gap-6 xl:gap-10 ${at.center}`}>
            {menu.links.map((link) => link.children ? (
              <NavDropdown key={link.href} item={link} />
            ) : (
              <Link
                key={link.href}
                href={link.href}
                hrefLang={link.hrefLang}
                onClick={scrollToHash(link.href)}
                className="whitespace-nowrap text-base font-medium text-muted-foreground transition-colors hover:text-foreground lg:text-lg"
                style={{ transitionDuration: "var(--transition-fast)" }}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Web app login (another origin: plain <a>, same tab) */}
          <a
            href={WEB_APP_LINKS.login}
            className={`me-4 hidden shrink-0 whitespace-nowrap text-base font-medium text-muted-foreground transition-colors hover:text-foreground lg:me-5 lg:text-lg ${at.inline}`}
            style={{ transitionDuration: "var(--transition-fast)" }}
          >
            {menu.login}
          </a>

          {/* CTA button — right side */}
          <Link
            href="/#download"
            hrefLang={menu.englishHrefLang}
            onClick={scrollToHash("/#download")}
            className={`hidden shrink-0 whitespace-nowrap rounded-full bg-primary px-5 py-2.5 text-base font-semibold text-primary-foreground transition-shadow hover:shadow-[var(--shadow-glow)] lg:px-7 lg:py-3 lg:text-lg ${at.inline}`}
            style={{ transitionDuration: "var(--transition-smooth)" }}
          >
            {menu.getStarted}
          </Link>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileOpen(true)}
            className={`ms-auto rounded-md p-2 text-foreground ${at.phoneOnly}`}
            aria-label={menu.open}
          >
            <Menu className="h-6 w-6" />
          </button>
        </nav>
      </header>

      {/* Mobile overlay — outside header to avoid backdrop-blur containing block */}
      {mobileOpen && (
        <div className={`fixed inset-0 z-50 ${at.phoneOnly}`}>
          <div
            className="absolute inset-0 bg-foreground/20 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
          />
          <div className="absolute end-0 top-0 flex h-full w-72 flex-col overflow-y-auto bg-card p-6 shadow-lg">
            <button
              onClick={() => setMobileOpen(false)}
              className="mb-8 self-end rounded-md p-2 text-foreground"
              aria-label={menu.close}
            >
              <X className="h-6 w-6" />
            </button>
            <div className="flex flex-col gap-4">
              {menu.links.map((link) => link.children ? (
                <NavDropdown key={link.href} item={link} mobile onNavigate={() => setMobileOpen(false)} />
              ) : (
                <Link
                  key={link.href}
                  href={link.href}
                  hrefLang={link.hrefLang}
                  onClick={(e) => {
                    setMobileOpen(false);
                    scrollToHash(link.href)(e);
                  }}
                  className="text-lg font-medium text-foreground transition-colors hover:text-primary"
                >
                  {link.label}
                </Link>
              ))}
              <a
                href={WEB_APP_LINKS.login}
                className="text-lg font-medium text-foreground transition-colors hover:text-primary"
              >
                {menu.login}
              </a>
            </div>
            <Link
              href="/#download"
              hrefLang={menu.englishHrefLang}
              onClick={(e) => {
                setMobileOpen(false);
                scrollToHash("/#download")(e);
              }}
              className="mt-6 rounded-full bg-primary px-5 py-2.5 text-center text-base font-semibold text-primary-foreground"
            >
              {menu.getStarted}
            </Link>
            <div className="mt-6">
              <AppStoreBadges placement="nav" size="sm" />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
